import { Link, createFileRoute, useParams } from "@tanstack/react-router";

import SharedChat from "@/components/SharedChat";
import { AppHeader } from "@/components/app-header";
import { RequireAuth } from "@/components/route-guards";
import { useAuth } from "@/lib/auth";
import { signOut } from "@/lib/platform-fetch";

/**
 * `/shared/$shareId` -- a conversation someone shared (docs/DECISIONS.md G87).
 *
 * `RequireAuth` and nothing more. Whether *this* caller may read *this* share is the server's
 * decision, re-read from the record on every open; a client-side check could only ever be a
 * second opinion that drifts. What the guard is for is the unauthenticated case: this platform
 * has no anonymous surface, so "anyone with the link" still means a signed-in colleague, and
 * landing here signed out should start sign-in rather than render a refusal.
 *
 * Wears the ordinary `AppHeader` -- logo, theme toggle, user menu -- for the same reason
 * `/settings/skills` does: the recipient is a signed-in colleague on their own platform, not a
 * visitor to a public page, and an unbranded transcript reads as an export someone emailed
 * them. "Back to chat" goes to their own conversations, not the sharer's.
 */
export const Route = createFileRoute("/shared/$shareId")({
  component: SharedRoute,
});

function SharedRoute() {
  return (
    <RequireAuth>
      <SharedPage />
    </RequireAuth>
  );
}

function SharedPage() {
  const { shareId } = useParams({ from: "/shared/$shareId" });
  const { user, hasAdminAccess, skillsEnabled } = useAuth();

  return (
    <main className="flex h-dvh flex-col">
      <AppHeader
        left={<Link to="/">&larr; Back to chat</Link>}
        userLabel={user?.email ?? user?.name ?? "unknown"}
        hasAdminAccess={hasAdminAccess}
        skillsEnabled={skillsEnabled}
        signOutAction={signOut}
      />
      <SharedChat shareId={shareId} />
    </main>
  );
}
